import Decimal from 'decimal.js';
import { useMemo } from 'react';
import { StyledDataSum } from '../../atoms/SettlementsDoneDataElement/StyledDataSum';
import { StyledText } from '../../atoms/Text/StyledText';
import { getString } from '../../../strings';
import { taxPercent } from '../../constants/pay';

const MonthSummary = ({ val }) => {
    const revenue = useMemo(
        () =>
            val && Object.keys(val).length
                ? Object.values(val)
                      .map((obj) => obj.price)
                      .reduce((prev, cur) => prev.plus(cur), new Decimal(0))
                : undefined,
        [val]
    );

    const trainerPayWithoutAdmin = useMemo(
        () =>
            val && Object.keys(val).length
                ? Object.values(val).reduce(
                      (prev, cur) => prev.plus(cur.is_admin ? 0 : cur.pay),
                      new Decimal(0)
                  )
                : undefined,
        [val]
    );

    const income = useMemo(
        () =>
            revenue && trainerPayWithoutAdmin ? revenue.minus(trainerPayWithoutAdmin) : undefined,
        [revenue, trainerPayWithoutAdmin]
    );

    const hoursAmount = useMemo(
        () =>
            val && Object.keys(val).length
                ? Object.values(val)
                      .map((obj) => obj.hours)
                      .reduce((prev, cur) => prev.plus(cur), new Decimal(0))
                : undefined,
        [val]
    );

    const managerHoursAmount = useMemo(
        () =>
            val && Object.keys(val).length
                ? Object.values(val)
                      .map((obj) => obj?.managerHours ?? 0)
                      .reduce((prev, cur) => prev.plus(cur), new Decimal(0))
                : new Decimal(0),
        [val]
    );
    const hoursPlusManagerHoursAmount = useMemo(
        () => (hoursAmount ?? new Decimal(0)).plus(managerHoursAmount ?? new Decimal(0)),
        [hoursAmount, managerHoursAmount]
    );

    const free = useMemo(
        () =>
            val && Object.keys(val).length
                ? Object.values(val)
                      .map((obj) => obj.free)
                      .reduce((prev, cur) => prev.plus(cur), new Decimal(0))
                : undefined,
        [val]
    );

    const paidHours = useMemo(
        () =>
            val && Object.keys(val).length
                ? Object.values(val)
                      .map((obj) => obj.paidHours)
                      .reduce((prev, cur) => prev.plus(cur), new Decimal(0))
                : undefined,
        [val]
    );

    const halfHours = useMemo(
        () =>
            val && Object.keys(val).length
                ? Object.values(val)
                      .map((obj) => obj.halfHours)
                      .reduce((prev, cur) => prev.plus(cur), new Decimal(0))
                : undefined,
        [val]
    );

    const free_hours_price = useMemo(
        () =>
            val && Object.keys(val).length
                ? Object.values(val)
                      .map((obj) => obj.free_hours_price)
                      .reduce((prev, cur) => prev.plus(cur), new Decimal(0))
                : undefined,
        [val]
    );

    const incomeAfterTaxes = useMemo(
        () =>
            income
                ? income.times(new Decimal(1).minus(new Decimal(taxPercent).dividedBy(100)))
                : undefined,
        [income]
    );

    const remainingTrainerPay = useMemo(() => {
        if (!val || !Object.keys(val).length) {
            return new Decimal(0);
        }

        return Object.values(val)
            .filter((obj) => obj && !obj.is_admin && obj.pay)
            .reduce((prev, cur) => {
                const totalDue = new Decimal(cur.pay);
                const totalPaid = cur.salary?.paidAmount
                    ? new Decimal(cur.salary.paidAmount)
                    : new Decimal(0);

                return prev.plus(totalDue.minus(totalPaid));
            }, new Decimal(0));
    }, [val]);

    return (
        <StyledDataSum>
            <div>
                <StyledText
                    hasdeclaredfontsize="18px"
                    hasdeclaredfontweight="600"
                    hasdeclaredlineheight="1.4em"
                    hasdeclaredfontcolor="#6786BE"
                    hasdeclaredtexttransform="uppercase"
                >
                    {getString('open_element_przychod')}:{' '}
                    <span style={{ color: '#000' }}>
                        {revenue && free_hours_price
                            ? revenue.dp() > 0
                                ? revenue.minus(free_hours_price).toFixed(2)
                                : revenue.minus(free_hours_price).toString()
                            : '0.00'}{' '}
                        PLN
                    </span>
                </StyledText>
            </div>
            <div>
                <StyledText
                    hasdeclaredfontsize="18px"
                    hasdeclaredfontweight="600"
                    hasdeclaredlineheight="1.4em"
                    hasdeclaredfontcolor="#6786BE"
                    hasdeclaredtexttransform="uppercase"
                >
                    {getString('open_element_godziny')}:{' '}
                    <span style={{ color: '#000' }}>
                        {paidHours ? paidHours.toString() : '0'} (D: {free ? free.toString() : '0'}{' '}
                        | P: {halfHours ? halfHours.toString() : '0'} | Ł:{' '}
                        {hoursAmount ? hoursAmount.toString() : '0'} | ŁD:{' '}
                        {hoursPlusManagerHoursAmount ? hoursPlusManagerHoursAmount.toString() : '0'}
                        )
                    </span>
                </StyledText>
            </div>
            <div>
                <StyledText
                    hasdeclaredfontsize="18px"
                    hasdeclaredfontweight="600"
                    hasdeclaredlineheight="1.4em"
                    hasdeclaredfontcolor="#6786BE"
                    hasdeclaredtexttransform="uppercase"
                >
                    {getString('open_element_dochód')}:{' '}
                    <span style={{ color: '#000' }}>
                        {income && incomeAfterTaxes
                            ? `${income.dp() > 0 ? income.toFixed(2) : income.toString()} (${
                                  incomeAfterTaxes.dp() > 0
                                      ? incomeAfterTaxes.toFixed(2)
                                      : incomeAfterTaxes.toString()
                              })`
                            : '0.00 (0.00)'}{' '}
                        PLN
                    </span>
                </StyledText>
            </div>
            <div>
                <StyledText
                    hasdeclaredfontsize="18px"
                    hasdeclaredfontweight="600"
                    hasdeclaredlineheight="1.4em"
                    hasdeclaredfontcolor="#6786BE"
                    hasdeclaredtexttransform="uppercase"
                >
                    {getString('open_element_wypłaty')}:{' '}
                    <span style={{ color: '#000' }}>
                        {trainerPayWithoutAdmin
                            ? trainerPayWithoutAdmin.dp() > 0
                                ? trainerPayWithoutAdmin.toFixed(2)
                                : trainerPayWithoutAdmin.toString()
                            : '0.00'}{' '}
                        PLN{' '}
                        {remainingTrainerPay.gt(0) &&
                            !remainingTrainerPay.equals(trainerPayWithoutAdmin) &&
                            `(${
                                remainingTrainerPay.dp() > 0
                                    ? remainingTrainerPay.toFixed(2)
                                    : remainingTrainerPay.toString()
                            } PLN)`}
                    </span>
                </StyledText>
            </div>
        </StyledDataSum>
    );
};

export default MonthSummary;
